/**
 * SessionExpiredPage - 세션 만료 안내 페이지
 *
 * 테이블 세션이 종료되었을 때 진단 가능한 고객용 결과를 표시합니다.
 *
 * @see Issue #220 - 세션 만료 타이머 UI
 */

import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, Home, QrCode } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { getQrSessionTranslation } from '@/i18n/qrSession';
import { ROUTES } from '@/config/routes';
import {
  clearSessionTerminalContext,
  createSessionTerminalContext,
  getSessionTerminalReference,
  SESSION_TERMINAL_CONTEXT_KEY,
  SESSION_TERMINAL_TTL_MS,
  validateSessionTerminalContext,
  type SessionTerminalContext,
  type SessionTerminalReason,
} from '@/lib/sessionTerminal';

function getRouterTerminal(locationState: unknown): unknown {
  if (typeof locationState !== 'object' || locationState === null) {
    return undefined;
  }

  return (locationState as { terminal?: unknown }).terminal;
}

function createUnknownTerminal(): SessionTerminalContext {
  return createSessionTerminalContext({
    reason: 'unknown',
    source: 'initial_entry',
    httpStatus: null,
    serverStatus: null,
    sessionId: null,
  });
}

function isExpiredRouterTerminal(
  value: unknown,
  now: number,
): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const occurredAt = (value as { occurredAt?: unknown }).occurredAt;
  if (typeof occurredAt !== 'string') {
    return false;
  }

  const occurredAtTimestamp = Date.parse(occurredAt);
  if (!Number.isFinite(occurredAtTimestamp)) {
    return false;
  }

  // occurredAt 시점에서는 TTL age가 0이므로 shared validator가 스키마만 검증한다.
  // 미래 시각은 아래 age가 음수가 되어 malformed fallback으로 처리된다.
  const schemaValid = validateSessionTerminalContext(value, occurredAtTimestamp);
  const age = now - occurredAtTimestamp;

  return schemaValid !== null && age >= SESSION_TERMINAL_TTL_MS;
}

interface StoredTerminalSnapshot {
  terminal: SessionTerminalContext | null;
  shouldClearStoredContext: boolean;
}

function readStoredTerminalSnapshot(now: number): StoredTerminalSnapshot {
  if (typeof window === 'undefined') {
    return { terminal: null, shouldClearStoredContext: false };
  }

  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return { terminal: null, shouldClearStoredContext: false };
  }

  let stored: string | null;
  try {
    stored = storage.getItem(SESSION_TERMINAL_CONTEXT_KEY);
  } catch {
    return { terminal: null, shouldClearStoredContext: false };
  }

  if (stored === null) {
    return { terminal: null, shouldClearStoredContext: false };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return { terminal: null, shouldClearStoredContext: true };
  }

  const terminal = validateSessionTerminalContext(parsed, now);
  return {
    terminal,
    shouldClearStoredContext: terminal === null,
  };
}

interface TerminalResolution {
  terminal: SessionTerminalContext;
  shouldClearStoredContext: boolean;
}

function resolveTerminalContext(locationState: unknown): TerminalResolution {
  const routerTerminal = getRouterTerminal(locationState);
  const now = Date.now();

  if (routerTerminal !== undefined) {
    const validatedRouterTerminal = validateSessionTerminalContext(routerTerminal, now);
    if (validatedRouterTerminal) {
      return {
        terminal: validatedRouterTerminal,
        shouldClearStoredContext: false,
      };
    }

    if (isExpiredRouterTerminal(routerTerminal, now)) {
      return {
        terminal: createUnknownTerminal(),
        shouldClearStoredContext: true,
      };
    }
  }

  const stored = readStoredTerminalSnapshot(now);
  return {
    terminal: stored.terminal ?? createUnknownTerminal(),
    shouldClearStoredContext: stored.shouldClearStoredContext,
  };
}

type TerminalAction = 'home' | 'qr';

const ACTION_BASE_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium transition-colors';
const ACTION_PRIMARY_CLASS =
  'bg-primary text-primary-foreground hover:bg-primary/90';
const ACTION_SECONDARY_CLASS =
  'border border-border bg-background text-foreground hover:bg-muted';

export function SessionExpiredPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useApp();
  const t = getQrSessionTranslation(locale, 'en');
  const resolution = useMemo(
    () => ({
      locationKey: location.key,
      ...resolveTerminalContext(location.state),
    }),
    [location.key, location.state],
  );
  const terminal = resolution.terminal;
  const reference = getSessionTerminalReference(terminal.eventId);

  useEffect(() => {
    if (resolution.shouldClearStoredContext) {
      clearSessionTerminalContext();
    }
  }, [
    location.state,
    resolution.locationKey,
    resolution.shouldClearStoredContext,
  ]);

  const titleByReason: Record<SessionTerminalReason, string> = {
    completed: t.sessionCompletedTitle,
    expired: t.sessionExpiredTitle,
    unauthorized: t.sessionInvalidTitle,
    not_found: t.sessionInvalidTitle,
    invalid: t.sessionInvalidTitle,
    unknown: t.sessionUnknownTitle,
  };

  const leaveTerminalPage = (path: string) => {
    clearSessionTerminalContext();
    navigate(path, { replace: true });
  };

  const actions: TerminalAction[] = terminal.reason === 'completed'
    ? ['home', 'qr']
    : ['qr', 'home'];

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] gap-6 px-6">
      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
        <Clock className="h-10 w-10 text-muted-foreground" />
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold text-foreground">
          {titleByReason[terminal.reason]}
        </h2>
        <p className="text-sm text-muted-foreground max-w-xs">{t.sessionExpiredDescription}</p>
      </div>

      <div className="flex w-full max-w-xs flex-col gap-3">
        {actions.map((action, index) => {
          const isPrimary = index === 0;
          const Icon = action === 'home' ? Home : QrCode;
          const label = action === 'home' ? t.sessionExpiredGoHome : t.rescanQr;
          const path = action === 'home' ? ROUTES.HOME : ROUTES.QR;

          return (
            <button
              key={action}
              type="button"
              data-variant={isPrimary ? 'primary' : 'secondary'}
              onClick={() => leaveTerminalPage(path)}
              className={`${ACTION_BASE_CLASS} ${
                isPrimary ? ACTION_PRIMARY_CLASS : ACTION_SECONDARY_CLASS
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        {t.sessionTerminalReferenceLabel}: {reference}
      </p>
    </div>
  );
}
